import React, { useCallback, useContext, useMemo, useState } from "react";
import styled from "styled-components";
import SubmissionPeriod from "src/types/SubmissionPeriod";

import Loader from "src/components/Loader";
import { Button, Theme } from "@120wateraudit/waterworks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox } from "semantic-ui-react";
import { faPenToSquare, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { formatDate, formatSubmissionPeriodType } from "src/utils/format";
import { useGetAllSubmissionPeriodsQuery } from "src/services";
import { useToggle } from "src/hooks/useToggle";
import CreateSubmissionPeriod from "./CreateSubmissionPeriodForm/CreateSubmissionPeriod";
import EditSubmissionPeriod from "./EditSubmissionPeriodForm/EditSubmissionPeriod";
import { ModalContext } from "../Modal";
import { useDeleteSubmissionPeriod, useUpdateSubmissionPeriod } from "./hooks";
import { useIsWrite } from "src/modules/User";

const ManageSubmissionPeriods: React.FC = (): JSX.Element => {
    const { isWrite } = useIsWrite();
    const [submissionPeriod, setSubmissionPeriod] =
        useState<SubmissionPeriod | null>();
    const [isEditMode, toggleEditMode] = useToggle();
    const [isCreateMode, toggleCreateMode] = useToggle();
    const { closeModal, openModal } = useContext(ModalContext);

    const {
        data: queryData,
        isFetching,
        refetch,
    } = useGetAllSubmissionPeriodsQuery({
        filters: {},
        page: 1,
        pageSize: 10000,
    });

    const submissionPeriods = queryData?.data ?? [];

    const activeSubmissionPeriods = useMemo(() => {
        return (
            submissionPeriods.filter(
                (submissionPeriod) => submissionPeriod.active && !submissionPeriod.sandbox
            ) ?? []
        );
    }, [submissionPeriods]);

    const inactiveSubmissionPeriods = useMemo(() => {
        const spd =
            submissionPeriods.filter(
                (submissionPeriod) => !submissionPeriod.active || submissionPeriod.sandbox
            ) ?? [];
        return spd;
    }, [submissionPeriods]);

    const onClose = async (): Promise<void> => {
        await refetch();
    };

    const { onDelete } = useDeleteSubmissionPeriod(onClose);
    const { onSubmit } = useUpdateSubmissionPeriod(onClose);

    const openDeleteSubmissionPeriod = useCallback(
        (submissionPeriodId: number) =>
            openModal("deleteSubmissionPeriodModal", {
                headerText: "Delete Submission Period",
                onConfirm: async () => {
                    await onDelete(submissionPeriodId);
                    closeModal();
                },
            }),
        [openModal, closeModal]
    );

    const openSubmissionPeriodStatusActiveSwitchModal = useCallback(
        (submissionPeriod: SubmissionPeriod) =>
            openModal("submissionPeriodStatusActiveSwitchModal", {
                headerText: "Submission Status",
                isCurrentlyActive: submissionPeriod.active,
                onConfirm: async () => {
                    await onSubmit(
                        {
                            ...submissionPeriod,
                            active: !submissionPeriod.active,
                        },
                        submissionPeriod.id
                    );
                    closeModal();
                },
            }),
        [openModal, closeModal]
    );

    return (
        <>
            {isCreateMode && (
                <CreateSubmissionPeriod
                    refetch={refetch}
                    toggleCreateMode={toggleCreateMode}
                />
            )}
            {isEditMode && (
                <EditSubmissionPeriod
                    refetch={refetch}
                    submissionPeriod={submissionPeriod}
                    toggleEditMode={toggleEditMode}
                />
            )}

            {!isCreateMode && !isEditMode && (
                <Wrapper>
                    {/* eslint-disable-next-line multiline-ternary */}
                    {isFetching ? (
                        <LoaderWrapper>
                            <Loader size="3x" />
                        </LoaderWrapper>
                    ) : (
                        <div>
                            <TopWrapper>
                                <Header>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <h3 style={{ fontSize: "24px", fontWeight: "600" }}>
                                            Manage Submission Periods
                                        </h3>
                                        {isWrite && (
                                            <AddNewButton onClick={toggleCreateMode}>
                                                <span
                                                    style={{
                                                        fontWeight: "400",
                                                        fontSize: "22px",
                                                        marginRight: "5px",
                                                    }}
                                                >
                                                    +
                                                </span>{" "}
                                                Create New Submission Period
                                            </AddNewButton>
                                        )}
                                    </div>
                                    <p style={{ fontSize: "15px", fontWeight: "400" }}>
                                        Submission periods organize and manage incoming submissions
                                        from your water systems. Multiple periods can be active simultaneously to support different submission types.
                                    </p>
                                </Header>
                            </TopWrapper>
                            <SubmissionPeriodsContainer>
                                {activeSubmissionPeriods.length > 0 && (
                                    <h4 style={{ margin: "20px 0 10px 0", fontSize: "18px", fontWeight: "500" }}>
                                        Active Submission Periods ({activeSubmissionPeriods.length})
                                    </h4>
                                )}
                                {activeSubmissionPeriods.map((submissionPeriod) => (
                                    <SubmissionPeriodRow key={submissionPeriod.id}>
                                        <Title
                                            style={{
                                                width: "230px",
                                                fontWeight: "400",
                                            }}
                                        >
                                            {submissionPeriod.name || "--"}
                                        </Title>
                                        <Title
                                            style={{
                                                width: "135px",
                                            }}
                                        >
                                            Type:{" "}
                                            <Text
                                                style={{
                                                    width: "135px",
                                                }}
                                            >
                                                {formatSubmissionPeriodType(submissionPeriod.submissionType)}
                                            </Text>
                                        </Title>
                                        <Title
                                            style={{
                                                marginRight: "5px",
                                            }}
                                        >
                                            Due Date:{" "}
                                            <Text>
                                                {formatDate(submissionPeriod.dueDate) || "--"}
                                            </Text>
                                        </Title>
                                        <Title
                                            style={{
                                                width: "210px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "1.5em",
                                            }}
                                        >
                                            Status:
                                            <Checkbox
                                                checked={submissionPeriod.active}
                                                toggle
                                                disabled={!isWrite}
                                                onChange={() => {
                                                    openSubmissionPeriodStatusActiveSwitchModal(
                                                        submissionPeriod
                                                    );
                                                }}
                                                aria-label={`Toggle ${submissionPeriod.name} submission period status`}
                                            />
                                            <Text
                                                style={{
                                                    color: submissionPeriod.active
                                                        ? Theme.palette.badgeSuccessText
                                                        : Theme.palette.badgeErrorText,
                                                    fontSize: "15px",
                                                    backgroundColor: submissionPeriod.active
                                                        ? Theme.palette.badgeSuccessBackground
                                                        : Theme.palette.badgeErrorBackground,
                                                    borderRadius: "4px",
                                                    padding: "2px 6px",
                                                }}
                                            >
                                                {submissionPeriod.active ? "Open" : "Closed"}
                                            </Text>
                                        </Title>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "1em",
                                            }}
                                        >
                                            {isWrite && (
                                                <>
                                                    <StyledFontAwesomeIcon
                                                        onClick={() => {
                                                            toggleEditMode();
                                                            setSubmissionPeriod(submissionPeriod);
                                                        }}
                                                        icon={faPenToSquare}
                                                        aria-label="Edit submission period"
                                                        size={"lg"}
                                                    />
                                                    <StyledFontAwesomeIcon
                                                        onClick={() => {
                                                            openDeleteSubmissionPeriod(submissionPeriod.id);
                                                        }}
                                                        icon={faTrashCan}
                                                        aria-label="Delete submission period"
                                                        size={"lg"}
                                                        color={Theme.palette.badgeErrorText}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </SubmissionPeriodRow>
                                ))}
                                {inactiveSubmissionPeriods.length > 0 && (
                                    <h4 style={{ margin: "20px 0 10px 0", fontSize: "18px", fontWeight: "500" }}>
                                        Inactive Submission Periods ({inactiveSubmissionPeriods.length})
                                    </h4>
                                )}
                                {inactiveSubmissionPeriods.map((submissionPeriod) => (
                                    <SubmissionPeriodRow key={submissionPeriod.id}>
                                        <Title
                                            style={{
                                                width: "230px",
                                                fontWeight: "400",
                                            }}
                                        >
                                            {submissionPeriod.name || "--"}
                                        </Title>
                                        <Title
                                            style={{
                                                width: "135px",
                                            }}
                                        >
                                            Type:{" "}
                                            <Text
                                                style={{
                                                    width: "135px",
                                                }}
                                            >
                                                {formatSubmissionPeriodType(submissionPeriod.submissionType)}
                                            </Text>
                                        </Title>
                                        <Title
                                            style={{
                                                marginRight: "5px",
                                            }}
                                        >
                                            Due Date:{" "}
                                            <Text>
                                                {formatDate(submissionPeriod.dueDate) || "--"}
                                            </Text>
                                        </Title>
                                        <Title
                                            style={{
                                                width: "210px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "1.5em",
                                            }}
                                        >
                                            Status:
                                            <Checkbox
                                                checked={submissionPeriod.active}
                                                toggle
                                                disabled={!isWrite}
                                                onChange={() => {
                                                    openSubmissionPeriodStatusActiveSwitchModal(
                                                        submissionPeriod
                                                    );
                                                }}
                                                aria-label={`Toggle ${submissionPeriod.name} submission period status`}
                                            />
                                            <Text
                                                style={{
                                                    color: submissionPeriod.active
                                                        ? Theme.palette.badgeSuccessText
                                                        : Theme.palette.badgeErrorText,
                                                    fontSize: "15px",
                                                    backgroundColor: submissionPeriod.active
                                                        ? Theme.palette.badgeSuccessBackground
                                                        : Theme.palette.badgeErrorBackground,
                                                    borderRadius: "4px",
                                                    padding: "2px 6px",
                                                }}
                                            >
                                                {submissionPeriod.active ? "Open" : "Closed"}
                                            </Text>
                                        </Title>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "1em",
                                            }}
                                        >
                                            {isWrite && (
                                                <>
                                                    <StyledFontAwesomeIcon
                                                        onClick={() => {
                                                            toggleEditMode();
                                                            setSubmissionPeriod(submissionPeriod);
                                                        }}
                                                        icon={faPenToSquare}
                                                        aria-label="Edit submission period"
                                                        size={"lg"}
                                                    />
                                                    <StyledFontAwesomeIcon
                                                        onClick={() => {
                                                            openDeleteSubmissionPeriod(submissionPeriod.id);
                                                        }}
                                                        icon={faTrashCan}
                                                        aria-label="Delete submission period"
                                                        size={"lg"}
                                                        color={Theme.palette.badgeErrorText}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </SubmissionPeriodRow>
                                ))}
                            </SubmissionPeriodsContainer>
                        </div>
                    )}
                </Wrapper>
            )}
        </>
    );
};

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 300;
`;

const Text = styled.span`
    font-weight: 400;
`;

const Wrapper = styled.div`
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
`;
const SubmissionPeriodsContainer = styled.div`
  display: grid;
  gap: 20px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 5rem;
`;

const SubmissionPeriodRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const AddNewButton = styled(Button)`
  font-size: 16px;
  font-weight: 600;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: black;
`;

export default ManageSubmissionPeriods;
